import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView, TextInput, Alert, ActivityIndicator, Image, Keyboard, Platform, Linking } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { ProfileUpdateData } from '../../lib/api/users';
import ThemedText from '../common/ThemedText';
import CredibilityTab from './CredibilityTab';
import { CustomPicker } from '../common/CustomPicker';
import * as ImagePicker from 'expo-image-picker';
import { getCountries, getCitiesForCountry } from '../../data/countriesAndCities';
import { storage } from '../../utils/storage';
import { apiBaseUrl } from '../../config/env';

type BusinessTab = 'details' | 'credibility';

interface BusinessDetailsProps {
  profile: any;
  isOwnProfile: boolean;
  isEditing: boolean;
  onEditPress: () => void;
  onSavePress: (data: ProfileUpdateData) => void;
  onCancelPress: () => void;
  onDeletePress: () => void;
  onWriteReview?: () => void;
  isSaving: boolean;
}

const BusinessDetails: React.FC<BusinessDetailsProps> = ({ 
  profile,
  isOwnProfile, 
  isEditing,
  onEditPress,
  onSavePress,
  onCancelPress,
  onDeletePress,
  onWriteReview,
  isSaving
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [activeTab, setActiveTab] = useState<BusinessTab>('details');

  // Form state
  const [formData, setFormData] = useState<ProfileUpdateData>({});

  // Get available cities based on selected country
  const availableCities = React.useMemo(() => {
    if (!formData.businessCountry) {
      return ['Other'];
    }
    return getCitiesForCountry(formData.businessCountry);
  }, [formData.businessCountry]);

  useEffect(() => {
    // Initialize form data from profile prop (use profile for both own and other users)
    const profileData = isOwnProfile ? user : profile;
    if (profileData) {
      setFormData({
        businessPhoto: (profileData as any).businessPhoto || '',
        businessTitle: (profileData as any).businessTitle || '',
        businessIndustry: (profileData as any).businessIndustry || '',
        businessType: (profileData as any).businessType || '',
        businessCountry: (profileData as any).businessCountry || '',
        businessPlace: (profileData as any).businessPlace || '',
        productsServices: (profileData as any).productsServices || '',
        noOfEmployees: (profileData as any).noOfEmployees || '',
        businessMobileNumber: (profileData as any).businessMobileNumber || '',
        linkedinId: (profileData as any).linkedinId || '',
        companyEmail: (profileData as any).companyEmail || '',
        businessWebsite: (profileData as any).businessWebsite || '',
        shareValue: (profileData as any).shareValue || '',
        businessLink: (profileData as any).businessLink || '',
        businessLocationFull: (profileData as any).businessLocationFull || '',
        aboutCompany: (profileData as any).aboutCompany || '',
      });
    }
  }, [profile, user, isOwnProfile]);

  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, (event) => {
      const height = event?.endCoordinates?.height ?? 320;
      setKeyboardVisible(true);
      setKeyboardOffset(height);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardVisible(false);
      setKeyboardOffset(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  // Check if business profile exists
  const profileData = isOwnProfile ? user : profile;
  const hasBusinessProfile = profileData && (
    (profileData as any).businessTitle ||
    (profileData as any).businessIndustry ||
    (profileData as any).companyEmail ||
    (profileData as any).businessPhoto
  );

  const renderField = (
    label: string, 
    fieldKey: keyof ProfileUpdateData,
    icon: string, 
    required: boolean = false,
    multiline: boolean = false,
    isDropdown: boolean = false,
    options: string[] = [],
    searchable: boolean = false
  ) => {
    const fieldValue = formData[fieldKey];
    const value = typeof fieldValue === 'string' ? fieldValue : '';
    
    return (
      <View style={styles.field}>
        <View style={styles.fieldHeader}>
          <Ionicons name={icon as any} size={18} color={theme.textSecondary} />
          <ThemedText
            style={[styles.fieldLabel, { color: theme.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {label}{required && ' *'}
          </ThemedText>
        </View>
        {isEditing ? (
          isDropdown ? (
            <View style={styles.pickerWrapper}>
              <CustomPicker
                selectedValue={value}
                onValueChange={(val) => setFormData(prev => ({ ...prev, [fieldKey]: val || '' }))}
                items={options.map(opt => ({ label: opt, value: opt }))}
                placeholder={`Select ${label.toLowerCase()}`}
                searchable={searchable}
                searchPlaceholder={searchable ? `Search ${label.toLowerCase()}` : undefined}
              />
            </View>
          ) : (
            <TextInput
              style={[
                multiline ? styles.textArea : styles.textInput,
                { 
                  color: theme.text,
                  backgroundColor: theme.backgroundSecondary,
                  borderColor: theme.border
                }
              ]}
              value={value}
              onChangeText={(text) => setFormData(prev => ({ ...prev, [fieldKey]: text }))}
              placeholder={`Enter ${label.toLowerCase()}`}
              placeholderTextColor={theme.textTertiary}
              multiline={multiline}
              numberOfLines={multiline ? 6 : 1}
              textAlignVertical={multiline ? 'top' : 'center'}
              keyboardType={fieldKey === 'companyEmail' ? 'email-address' : 'default'}
              autoCapitalize="none"
            />
          )
        ) : (
          // Show as clickable link for Google Maps
          fieldKey === 'businessLocationFull' && value ? (
            <TouchableOpacity 
              onPress={async () => {
                const supported = await Linking.canOpenURL(value);
                if (supported) {
                  await Linking.openURL(value);
                } else {
                  Alert.alert('Error', 'Unable to open Google Maps link');
                }
              }}
              activeOpacity={0.7}
            >
              <View style={styles.mapLinkContainer}>
                <Ionicons name="map" size={16} color={theme.accent} />
                <ThemedText style={[styles.mapLinkText, { color: theme.accent }]} numberOfLines={1}>
                  Open in Maps
                </ThemedText>
                <Ionicons name="open-outline" size={14} color={theme.accent} />
              </View>
            </TouchableOpacity>
          ) : (
            <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
              {value || 'Not specified'}
            </ThemedText>
          )
        )}
      </View>
    );
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData.businessTitle?.trim()) {
      Alert.alert('Required Field', 'Please enter business name');
      return;
    }
    if (!formData.businessIndustry) {
      Alert.alert('Required Field', 'Please select industry');
      return;
    }
    if (!formData.businessType) {
      Alert.alert('Required Field', 'Please select business type');
      return;
    }
    if (!formData.businessCountry) {
      Alert.alert('Required Field', 'Please select country');
      return;
    }
    if (!formData.businessPlace) {
      Alert.alert('Required Field', 'Please select city');
      return;
    }
    if (!formData.companyEmail?.trim()) {
      Alert.alert('Required Field', 'Please enter company email');
      return;
    }
    
    // Validate email format
    if (!validateEmail(formData.companyEmail)) {
      Alert.alert('Invalid Email', 'Please enter a valid email address');
      return;
    }

    onSavePress(formData);
  };

  const handlePickImage = async () => {
    // Photo picker on Android 13+ uses system UI - no READ_MEDIA_* permissions needed

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      const localUri = result.assets[0].uri;
      
      try {
        // Upload image to server and get cloud URL
        const cloudUrl = await uploadImageToServer(localUri);
        setFormData(prev => ({ ...prev, businessPhoto: cloudUrl }));
        Alert.alert('Success', 'Business photo uploaded successfully');
      } catch (error: any) {
        console.error('Error uploading business photo:', error);
        Alert.alert('Upload Failed', error.message || 'Failed to upload business photo');
      }
    }
  };

  const uploadImageToServer = async (uri: string): Promise<string> => {
    console.log('🔄 Starting business photo upload...', uri);
    
    const token = await storage.getToken();
    if (!token) {
      throw new Error('No authentication token');
    }

    // Create form data
    const formData = new FormData();
    const filename = uri.split('/').pop() || 'business_photo.jpg';
    const match = /\.(\w+)$/.exec(filename);
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    console.log('📁 File info:', { filename, type });

    // Handle web differently - need to fetch the blob and create a File
    if (Platform.OS === 'web') {
      const response = await fetch(uri);
      const blob = await response.blob();
      const file = new File([blob], filename, { type });
      formData.append('image', file);
    } else {
      formData.append('image', {
        uri,
        name: filename,
        type,
      } as any);
    }

    console.log('☁️ Uploading to:', `${apiBaseUrl}/api/upload/image`);

    // Upload to backend
    // IMPORTANT: Don't set Content-Type header - let browser set it with boundary for multipart
    const response = await fetch(`${apiBaseUrl}/api/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    console.log('📥 Upload response:', data);
    
    if (!data.success || !data.url) {
      throw new Error(data.message || 'Failed to upload image');
    }

    console.log('✅ Upload successful! URL:', data.url);
    return data.url;
  };

  return (
    <View style={styles.wrapper}>
      {!hasBusinessProfile && !isEditing && isOwnProfile ? (
        // Empty state - no business profile yet
        <View style={[styles.emptyStateContainer, { backgroundColor: theme.card }]}>
          <Ionicons name="business-outline" size={64} color={theme.textTertiary} />
          <ThemedText style={[styles.emptyTitle, { color: theme.text }]}>
            No Business Profile Yet
          </ThemedText>
          <ThemedText style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
            Create your business profile to showcase your company and services
          </ThemedText>
          <TouchableOpacity
            style={[styles.createButton, { backgroundColor: theme.accent }]}
            onPress={onEditPress}
            activeOpacity={0.8}
          >
            <Ionicons name="add-circle-outline" size={20} color="#FFFFFF" />
            <ThemedText style={styles.createButtonText}>Create Business Profile</ThemedText>
          </TouchableOpacity>
        </View>
      ) : (
        // Show business profile with tabs
        <>
          {/* Tab Bar */}
          <View style={[styles.tabBar, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'details' && { borderBottomColor: theme.accent, borderBottomWidth: 2 }]}
              onPress={() => setActiveTab('details')}
              activeOpacity={0.7}
            >
              <Ionicons 
                name="briefcase-outline" 
                size={20} 
                color={activeTab === 'details' ? theme.accent : theme.textSecondary} 
              />
              <ThemedText style={[
                styles.tabText, 
                { color: activeTab === 'details' ? theme.accent : theme.textSecondary }
              ]}>
                Details
              </ThemedText>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.tab, activeTab === 'credibility' && { borderBottomColor: theme.accent, borderBottomWidth: 2 }]}
              onPress={() => setActiveTab('credibility')}
              activeOpacity={0.7}
            >
              <Ionicons 
                name="star-outline" 
                size={20} 
                color={activeTab === 'credibility' ? theme.accent : theme.textSecondary} 
              />
              <ThemedText style={[
                styles.tabText, 
                { color: activeTab === 'credibility' ? theme.accent : theme.textSecondary }
              ]}>
                Credibility
              </ThemedText>
            </TouchableOpacity>
          </View>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            // Details Tab
            <>
              <View style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border }]}>
                {/* Content */}
                <ScrollView 
                  style={styles.contentContainer}
                  contentContainerStyle={{
                    paddingBottom: keyboardVisible ? keyboardOffset + 32 : 32,
                  }}
                  showsVerticalScrollIndicator={false}
                  keyboardShouldPersistTaps="handled"
                  automaticallyAdjustKeyboardInsets={false}
                  contentInsetAdjustmentBehavior="never"
                >
                  <View style={styles.content}>
                    {/* Business Profile Photo */}
                    <View style={styles.profilePhotoSection}>
                      <TouchableOpacity
                        style={[styles.largeProfilePhoto, { backgroundColor: theme.border, borderColor: theme.accent, borderWidth: isEditing ? 2 : 0 }]}
                        onPress={isEditing ? handlePickImage : undefined}
                        activeOpacity={isEditing ? 0.7 : 1}
                      >
                        {formData.businessPhoto ? (
                          <Image source={{ uri: formData.businessPhoto }} style={styles.profilePhotoImage} />
                        ) : (
                          <View style={styles.photoPlaceholder}>
                            <Ionicons name="camera" size={48} color={theme.textTertiary} />
                            {isEditing && (
                              <ThemedText style={[styles.photoPlaceholderText, { color: theme.textTertiary }]}>
                                Tap to upload
                              </ThemedText>
                            )}
                          </View>
                        )}
                        {isEditing && formData.businessPhoto && (
                          <View style={[styles.photoEditBadge, { backgroundColor: theme.accent }]}>
                            <Ionicons name="camera" size={16} color="#FFFFFF" />
                          </View>
                        )}
                      </TouchableOpacity>
                      {isEditing && (
                        <ThemedText style={[styles.photoLabel, { color: theme.textSecondary }]}>
                          Business Logo/Photo (Optional)
                        </ThemedText>
                      )}
                    </View>

                    {renderField('Business Name', 'businessTitle', 'briefcase-outline', true)}
                    {renderField('Industry', 'businessIndustry', 'business-outline', true, false, true, [
                      'Technology',
                      'Finance',
                      'Healthcare',
                      'Education',
                      'Retail',
                      'Manufacturing',
                      'Real Estate',
                      'Hospitality',
                      'Transportation',
                      'Entertainment',
                      'Agriculture',
                      'Construction',
                      'Consulting',
                      'Marketing',
                      'Other'
                    ], true)}
                    {renderField('Type', 'businessType', 'list-outline', true, false, true, [
                      'Startup',
                      'Corporate',
                      'Small Business',
                      'Enterprise',
                      'Non-Profit',
                      'Sole Proprietorship',
                      'Partnership',
                      'LLC'
                    ])}
                    {renderField('Country', 'businessCountry', 'globe-outline', true, false, true, getCountries(), true)}
                    {renderField('City', 'businessPlace', 'location-outline', true, false, true, availableCities, true)}
                    {renderField('Products / Services', 'productsServices', 'cube-outline', false)}
                    {renderField('No of Employees', 'noOfEmployees', 'people-outline', false)}
                    {renderField('Mobile Number', 'businessMobileNumber', 'call-outline', false)}
                    {renderField('Company Email Address', 'companyEmail', 'mail-outline', true)}
                    {renderField('Website', 'businessWebsite', 'link-outline', false)}
                    {renderField('Share Value', 'shareValue', 'cash-outline', false)}
                    {renderField('Google Maps Link', 'businessLocationFull', 'map-outline', false)}
                    {renderField('Write About Company', 'aboutCompany', 'document-text-outline', false, true)}
                    
                    {/* Save/Cancel/Delete Buttons at Bottom */}
                    {isOwnProfile && isEditing && (
                      <View style={[
                        styles.footerButtons,
                        {
                          paddingBottom: insets.bottom + 16
                        }
                      ]}>
                  {hasBusinessProfile && (
                    <TouchableOpacity
                      style={[styles.deleteButton, { backgroundColor: '#EF4444' }]}
                      onPress={onDeletePress}
                      activeOpacity={0.7}
                    >
                      <Ionicons name="trash-outline" size={18} color="#FFFFFF" />
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.cancelButton, { 
                      backgroundColor: theme.backgroundSecondary,
                      borderColor: theme.border
                    }]}
                    onPress={onCancelPress}
                    activeOpacity={0.7}
                  >
                    <ThemedText style={[styles.cancelButtonText, { color: theme.text }]}>Cancel</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.saveButton, { backgroundColor: theme.accent }]}
                    onPress={handleSave}
                    activeOpacity={0.7}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <ActivityIndicator color="#FFFFFF" size="small" />
                    ) : (
                      <ThemedText style={styles.saveButtonText}>Save</ThemedText>
                    )}
                  </TouchableOpacity>
                      </View>
                    )}
                  </View>
                </ScrollView>
              </View>
            </>
          ) : (
            // Credibility Tab
            <CredibilityTab 
              userId={profile?.id || profile?.userId || user?.userId || ''} 
              isOwnProfile={isOwnProfile}
              onWriteReview={onWriteReview}
            />
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    marginHorizontal: 8,
    marginBottom: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  footerButtons: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 16,
  },
  deleteButton: {
    width: 50,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1.5,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  contentContainer: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
    marginHorizontal: 8,
    marginBottom: 12,
    borderRadius: 8,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  createButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  profilePhotoSection: {
    alignItems: 'center',
    marginBottom: 24,
    paddingTop: 8,
  },
  largeProfilePhoto: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  photoPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoPlaceholderText: {
    fontSize: 12,
    marginTop: 8,
    textAlign: 'center',
  },
  photoLabel: {
    fontSize: 13,
    marginTop: 8,
    textAlign: 'center',
  },
  profilePhotoImage: {
    width: '100%',
    height: '100%',
    borderRadius: 60,
  },
  photoEditBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#FFFFFF',
    zIndex: 10,
  },
  field: {
    marginBottom: 20,
  },
  fieldHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginLeft: 8,
    flex: 1,
    flexShrink: 1,
  },
  fieldValue: {
    fontSize: 16,
    marginLeft: 26,
  },
  mapLinkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 26,
    marginTop: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255, 170, 0, 0.1)',
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  mapLinkText: {
    fontSize: 15,
    fontWeight: '600',
  },
  textInput: {
    fontSize: 16,
    marginLeft: 26,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  textArea: {
    fontSize: 16,
    marginLeft: 26,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    minHeight: 120,
  },
  pickerWrapper: {
    marginLeft: 26,
  },
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    marginHorizontal: 8,
    marginTop: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' ? { position: 'sticky' as any, top: 0, zIndex: 10, backgroundColor: 'inherit' } : {}),
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    gap: 8,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
  },
  writeReviewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    gap: 6,
    marginLeft: 8,
  },
  writeReviewButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  emptyReviewsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
});

export default BusinessDetails;
